// O TRADUTOR DO ARQUIVO MENSAL DA ASSALCE (#165, ADR-0013).
//
// So' forma: le as 38 posicoes e devolve a linha informada. Nao casa matricula
// com cadastro, nao olha rubrica nem credor, nao decide o que entra no
// movimento — isso e' da borda, na API, que conhece o cadastro. Aqui, como em
// todo adaptador: puro, nao lanca por conteudo e nao perde linha em silencio.
import { lerCompetencia, lerIntervaloCompetencia } from "../forma/data.js";
import { lerLarguraFixa } from "../forma/largura-fixa.js";
import { lerNumero } from "../forma/numero.js";
import { relatorioConversaoVazio } from "../relatorio.js";
import { ASSALCE_ENTRADA_VERSAO, CAMPOS_ASSALCE, campoAssalce, LARGURA_LINHA_ASSALCE, NOME_ARQUIVO_ASSALCE, VALOR_ASSALCE, } from "./assalce-colunas.js";
const CODIGO = "assalce-entrada";
/** Papel do arquivo no relatorio (o mesmo de `ARQUIVOS_ASSALCE_ENTRADA`). */
const ARQUIVO = "consignacoes";
/** A forma de uma linha: matricula, FF, rubrica, valor e referencia, separados por espaco. */
const FORMA_DA_LINHA = /^\d{6} \d{2} \S{3} \d{7},\d{2} .{13}$/;
/** O trecho de um campo na linha, pelas posicoes de `CAMPOS_ASSALCE`. */
function trecho(texto, nome) {
    const c = campoAssalce(nome);
    return texto.slice(c.inicio - 1, c.inicio - 1 + c.largura);
}
/** Onde acaba o valor: o que vem depois e' so' a referencia, que pode vir em branco. */
const FIM_DO_VALOR = campoAssalce("valor").inicio - 1 + campoAssalce("valor").largura;
/** Os arquivos do envio que tem o nome do arquivo da ASSALCE. */
function doLayout(arquivos) {
    return arquivos.filter((a) => NOME_ARQUIVO_ASSALCE.test(a.nome));
}
/** AAAAMM do nome, ou `null` quando o nome traz uma competencia que nao existe (ASS202613). */
function competenciaDoNome(nome) {
    const m = NOME_ARQUIVO_ASSALCE.exec(nome);
    return m ? lerCompetencia(m[1], "aaaamm") : null;
}
/**
 * As linhas com dado, cada uma com o numero que tem no arquivo. Linha em
 * branco e' ignorada em qualquer posicao (o documento do layout diz isso).
 *
 * A linha que perdeu so' os espacos do fim — a referencia, que vem em branco
 * fora das averbacoes com prazo — volta a ter as 38 posicoes, e a que GANHOU
 * espaco ou tabulacao depois da posicao 38 perde o excesso. Extracao de
 * `.rar`, FTP em modo texto e editores fazem as duas coisas; sem isto, todo
 * plano de saude do arquivo seria descartado como linha torta.
 */
function linhasDe(texto) {
    const saida = [];
    for (const [i, bruta] of texto.replace(/\r\n?/g, "\n").split("\n").entries()) {
        if (bruta.trim() === "")
            continue;
        const cortada = bruta.length >= FIM_DO_VALOR && bruta.length < LARGURA_LINHA_ASSALCE
            && bruta.slice(FIM_DO_VALOR).trim() === "";
        const sobrando = bruta.length > LARGURA_LINHA_ASSALCE && bruta.slice(LARGURA_LINHA_ASSALCE).trim() === "";
        saida.push({
            numero: i + 1,
            texto: cortada ? bruta.padEnd(LARGURA_LINHA_ASSALCE, " ") : sobrando ? bruta.slice(0, LARGURA_LINHA_ASSALCE) : bruta,
        });
    }
    return saida;
}
function naForma(linha) {
    return linha.length === LARGURA_LINHA_ASSALCE && FORMA_DA_LINHA.test(linha);
}
function recusa(motivo, encontrados = [], faltando = []) {
    return { reconhecido: false, layout: CODIGO, encontrados, faltando, motivo };
}
function reconhecer(arquivos) {
    const deles = doLayout(arquivos);
    if (deles.length === 0) {
        return recusa("o arquivo da ASSALCE se chama ASS{AAAAMM}.TXT (ex.: ASS202609.TXT)", [], ["ASS{AAAAMM}.TXT"]);
    }
    // Um arquivo por envio: com dois, qual e' o do mes? Traduzir so' o primeiro
    // faria as linhas do outro sumirem sem aviso.
    if (deles.length > 1) {
        return recusa(`${deles.map((a) => a.nome).join(", ")}: envie um arquivo da ASSALCE por vez, o da competencia que se quer importar`, deles.map((a) => a.nome));
    }
    const arquivo = deles[0];
    if (!competenciaDoNome(arquivo.nome)) {
        return recusa(`${arquivo.nome}: a competencia do nome nao existe (o formato e' ASS{AAAAMM}.TXT, mes de 01 a 12)`, [arquivo.nome]);
    }
    // Conteudo, alem do nome: um arquivo de outro layout renomeado nao e' dela.
    // Basta METADE das linhas ter a forma — uma linha torta no meio e' descarte
    // com motivo na traducao, nao recusa do arquivo inteiro, e um arquivo de duas
    // linhas com uma torta ainda e' dela.
    const linhas = linhasDe(arquivo.texto);
    const certas = linhas.filter((l) => naForma(l.texto)).length;
    if (linhas.length === 0 || certas * 2 < linhas.length) {
        return recusa(`${arquivo.nome} tem o nome do arquivo da ASSALCE, mas as linhas nao sao de ${LARGURA_LINHA_ASSALCE} posicoes (matricula, folha, codigo, valor e referencia)`, [arquivo.nome]);
    }
    return { reconhecido: true, layout: CODIGO, encontrados: [arquivo.nome], faltando: [] };
}
function traduzir(arquivos) {
    // O que `reconhecer` recusa pelo NOME, `traduzir` nao traduz por conta
    // propria: com dois arquivos da ASSALCE, escolher o primeiro faria as linhas
    // do outro sumirem; com competencia impossivel no nome, nao ha' mes para
    // carimbar. Nos dois casos nada e' traduzido, e o aviso diz por que — o
    // arquivo dela nunca e' chamado de "nao e' o arquivo da ASSALCE".
    const deles = doLayout(arquivos);
    const arquivo = deles.length === 1 ? deles[0] : undefined;
    const competencia = arquivo ? competenciaDoNome(arquivo.nome) ?? "" : "";
    const relatorio = relatorioConversaoVazio({ codigo: CODIGO, versao: ASSALCE_ENTRADA_VERSAO }, competencia);
    if (deles.length > 1) {
        relatorio.avisos.push(`${deles.map((a) => a.nome).join(", ")}: envie um arquivo da ASSALCE por vez, o da competencia que se quer importar; nenhum foi traduzido`);
    }
    if (arquivo && !competencia) {
        relatorio.avisos.push(`${arquivo.nome}: a competencia do nome nao existe (mes de 01 a 12); nenhuma linha foi traduzida`);
    }
    // O que veio junto e nao e' dela fica registrado: o operador ve que foi ignorado.
    for (const outro of arquivos) {
        if (!deles.includes(outro))
            relatorio.avisos.push(`${outro.nome} ignorado: nao e' o arquivo da ASSALCE`);
    }
    const linhas = [];
    const lidas = arquivo && competencia ? linhasDe(arquivo.texto) : [];
    let origemCentavos = 0;
    for (const { numero, texto } of lidas) {
        const descartar = (motivo) => relatorio.descartadas.push({ arquivo: ARQUIVO, linha: numero, matricula: trecho(texto, "matricula"), motivo });
        // O total de ORIGEM e' a soma de todo valor legivel do arquivo, aceito ou
        // descartado: e' o numero que se compara com o total que a consignataria
        // declara. So' a linha curta demais para ter o campo fica de fora.
        const reais = texto.length >= FIM_DO_VALOR ? lerNumero(trecho(texto, "valor"), VALOR_ASSALCE) : null;
        const centavos = reais === null ? null : Math.round(reais * 100);
        if (centavos !== null)
            origemCentavos += centavos;
        // Uma linha torta nao derruba o arquivo: vai para o relatorio com motivo,
        // e as outras 1.500 seguem.
        if (texto.length !== LARGURA_LINHA_ASSALCE) {
            descartar(`a linha tem ${texto.length} posicoes, e o layout tem ${LARGURA_LINHA_ASSALCE} posicoes`);
            continue;
        }
        if (centavos === null) {
            descartar(`valor "${trecho(texto, "valor")}" ilegivel: o formato e' 0006604,10`);
            continue;
        }
        if (!FORMA_DA_LINHA.test(texto)) {
            descartar("fora do formato: matricula (6), folha (2), codigo (3), valor (10) e referencia (13), separados por espaco");
            continue;
        }
        // Zero nao e' desconto: a ALECE encerra por OMISSAO, e o movimento dela
        // nunca teve linha zerada. Setembro trouxe uma (779, folha 03).
        if (centavos === 0) {
            descartar("valor zero: para encerrar, a consignataria omite a linha");
            continue;
        }
        // Referencia presente e ilegivel descarta: tomada como ausente, a averbacao
        // com prazo viraria recorrente e continuaria descontando depois do fim.
        const campos = lerLarguraFixa(texto, CAMPOS_ASSALCE);
        const textoReferencia = (campos.referencia ?? "").trim();
        const referencia = textoReferencia ? lerIntervaloCompetencia(textoReferencia) : null;
        if (textoReferencia && !referencia) {
            descartar(`referencia "${textoReferencia}" ilegivel: o formato e' MM/AA a MM/AA, com o fim depois do inicio`);
            continue;
        }
        linhas.push({
            linha: numero,
            ff: campos.folha,
            ffOriginal: trecho(texto, "folha"),
            matricula: campos.matricula,
            matriculaOriginal: trecho(texto, "matricula"),
            rubrica: campos.rubrica,
            valorCentavos: centavos,
            referencia,
        });
    }
    // O balanco que a conformidade (C4) confere: lidas = aceitas + descartadas.
    relatorio.lidos.push({ arquivo: ARQUIVO, linhas: lidas.length });
    relatorio.gerados.push({ arquivo: ARQUIVO, linhas: linhas.length });
    relatorio.totais = {
        origem: { valorCentavos: origemCentavos },
        gerado: { valorCentavos: linhas.reduce((s, l) => s + l.valorCentavos, 0) },
    };
    // O preco declarado do layout: o arquivo nao traz CPF nem nome. Os dois vem
    // do cadastro, pela matricula — e e' a borda que casa, porque so' ela o tem.
    if (linhas.length > 0) {
        relatorio.naoInformaveis.push({ campo: "cpf", motivo: "o arquivo da ASSALCE nao traz CPF; ele vem do cadastro pela matricula", linhasAfetadas: linhas.length }, { campo: "nome", motivo: "o arquivo da ASSALCE nao traz nome; ele vem do cadastro pela matricula", linhasAfetadas: linhas.length });
    }
    const matriculas = linhas.filter((l) => l.matricula !== l.matriculaOriginal).length;
    const folhas = linhas.filter((l) => l.ff !== l.ffOriginal).length;
    if (matriculas > 0) {
        relatorio.normalizados.push({ campo: "matricula", regra: "zeros a esquerda removidos (001476 vira 1476)", quantidade: matriculas });
    }
    if (folhas > 0) {
        relatorio.normalizados.push({ campo: "folha", regra: "zero a esquerda removido (03 vira 3)", quantidade: folhas });
    }
    return { competencia, linhas, relatorio };
}
export const adaptadorAssalceEntrada = { codigo: CODIGO, reconhecer, traduzir };
//# sourceMappingURL=assalce-entrada.js.map